/**
 * First we will load all of this project's JavaScript dependencies which
 * includes Vue and other libraries. It is a great starting point when
 * building robust, powerful web applications using Vue and Laravel.
 */

import "./bootstrap";
import "./cookieconsent-config";
import { createApp } from "vue";
import vSelect from "vue-select";
import VueClickAway from "vue3-click-away";

import { setupGlide } from "./partials/setupGlide";
import { setupLazyLoad } from "./partials/setupLazyLoad";
import { setupScrollPositionEvents } from "./partials/setupScrollPositionEvents";
import VueGoogleMaps from "@fawmi/vue-google-maps";
import globalFunctions from "./goobal-functions.js";
import Clipboard from 'v-clipboard'
import RoutesPlugin from './routes.js';

/**
 * Next, we will create a fresh Vue application instance. You may then begin
 * registering components with the application instance so they are ready
 * to use in your application's views. An example is included for you.
 */

const app = createApp({
    data() {
        return {
            mobileSearchMode: "list", // initial mode
            screenWidth: window.innerWidth, // capture screen width
            isMobileViewport: window.innerWidth < 768, // check if mobile viewport
        };
    },
    computed: {
        map() {
            if (this.screenWidth < 1024) {
                return this.mobileSearchMode === "map";
            } else {
                // always show the map if screen size >= 1024 or "lg" tailwind viewport
                return true;
            }
        },
        list() {
            if (this.screenWidth < 1024) {
                return this.mobileSearchMode === "list";
            } else {
                // always show the list if screen size >= 1024 or "lg" tailwind viewport
                return true;
            }
        },
    },
    created() {
        window.addEventListener("resize", this.updateWidth);
    },
    beforeUnmount() {
        window.removeEventListener("resize", this.updateWidth);
    },
    methods: {
        updateWidth() {
            this.screenWidth = window.innerWidth;
            this.isMobileViewport = this.screenWidth < 768;
        },
        toggleDisplay(value) {
            this.mobileSearchMode = value;
        },
    },
});

import ExampleComponent from "./components/ExampleComponent.vue";

import MyListings from "./components/MyListings.vue";
import MyListing from "./components/MyListing.vue";
import EditPersonalData from "./components/EditPersonalData.vue";
import EditLocationData from "./components/EditLocationData.vue";
import EditContactData from "./components/EditContactData.vue";
import ChangeProfilePicture from "./components/ChangeProfilePicture.vue";
import ProfileSettings from "./components/ProfileSettings.vue";

import Favourite from "./components/Favourite.vue";
import DeleteListing from "./components/DeleteListing.vue";
import LightBox from "./components/LightBox.vue";
import MainSearch from "./components/MainSearch/MainSearch.vue";
import MainSearchLandingActions from "./components/MainSearch/MainSearchLandingActions.vue";
import FormRadio from "./components/Form/FormRadio.vue";
import FormInput from "./components/Form/FormInput.vue";
import Checkbox from "./components/Form/Checkbox.vue";
import CsrfToken from "./components/Form/CsrfToken.vue";
import Icon from "./components/Common/Icon/Icon.vue";

import SearchCriteriaSave from "./components/SearchCriteriaSave.vue";
import SearchCriteriaEdit from "./components/SearchCriteriaEdit.vue";
import SearchCriteriaCreate from "./components/SearchCriteriaCreate.vue";
import SearchCriteriaDelete from "./components/SearchCriteriaDelete.vue";
import SearchFiltersAdditional from "./components/SearchFiltersAdditional.vue";
import SearchSort from "./components/SearchSort.vue";

import SearchFields from "./components/SearchFields/SearchFields.vue";
import SearchLightbox from "./components/Common/SearchLightbox.vue";

import FloorSelection from "./components/Common/FloorSelection.vue";

import Login from "./components/Auth/Login.vue";
import RegisterHead from "./components/registration/RegisterHead.vue";
import RegisterStepper from "./components/registration/RegisterStepper.vue";

import SubmitPhone from "./components/registration/SubmitPhone.vue";
import SubmitVerificationCode from "./components/registration/SubmitVerificationCode.vue";
import CreateAccount from "./components/registration/CreateAccount.vue";
import SubmitEmail from "./components/registration/SubmitEmail.vue";
import EmailSent from "./components/registration/EmailSent.vue";
import EmailConfirmed from "./components/registration/EmailConfirmed.vue";
import SetupPassword from "./components/registration/SetupPassword.vue";
import SubmitPersonalData from "./components/registration/SubmitPersonalData.vue";
import SubmitLocationData from "./components/registration/SubmitLocationData.vue";
import UploadCustomerPicture from "./components/registration/UploadCustomerPicture.vue";
import Welcome from "./components/registration/Welcome.vue";

import AddListingHead from "./components/Listing/AddListingHead.vue";
import AddListingWizard from "./components/Listing/AddListing.vue";

import ListingPriceInformation from "./components/Listing/ListingPriceInformation.vue";
import ListingPriceHistory from "./components/Listing/ListingPriceHistory.vue";
import ListingContact from "./components/Listing/ListingContact.vue";
import ListingShare from "./components/Listing/ListingShare.vue";
import ListingSave from "./components/Listing/ListingSave.vue";
import ListingGoogleMaps from "./components/Listing/ListingGoogleMaps.vue";
import SearchResultsGoogleMaps from "./components/Listing/SearchResultsGoogleMaps.vue";
import ActivateListing from "./components/Listing/ActivateListing.vue";

import Header from "./components/Header.vue";
import HeaderMobileBottomNavigation from "./components/HeaderMobileBottomNavigation.vue";
import Accordion from "./components/Common/Accordion/Accordion.vue";
import Dropdown from "./components/Common/Dropdown/Dropdown.vue";
import DropdownSm from "./components/Common/Dropdown/DropdownSm.vue";
import SessionMessage from "./components/Common/SessionMessage.vue";
import Alert from "./components/Common/Alerts/Alert.vue";
import CreateListingSimpleWizard from "./components/CreateListingSimple/AddListingSimple.vue"
import CarouselItem from "./components/CarouselItem.vue"
import AcceptTermsAndConditions from "./components/Auth/AcceptTermsAndConditions.vue";

app.component("v-carousel-item", CarouselItem);
app.component("v-accept-terms-and-conditions", AcceptTermsAndConditions);
app.component("v-add-listing-simple-wizard", CreateListingSimpleWizard);

// Our components
app.component("v-example-component", ExampleComponent);
app.component("v-favourite", Favourite);
app.component("v-delete-listing", DeleteListing);
app.component("v-header", {
    ...Header,
    emits: ["mobile-search-mode-toggled"], // catch the emitted event here
});
app.component(
    "v-header-mobile-bottom-navigation",
    HeaderMobileBottomNavigation
);
app.component("v-main-search", MainSearch);
app.component("v-main-search-landing-actions", MainSearchLandingActions);

app.component("v-lightbox", LightBox);

app.component("v-search-criteria-save", SearchCriteriaSave);
app.component("v-search-criteria-edit", SearchCriteriaEdit);
app.component("v-search-criteria-create", SearchCriteriaCreate);
app.component("v-search-criteria-delete", SearchCriteriaDelete);

app.component("v-search-filters-additional", SearchFiltersAdditional);
app.component("v-search-sort", SearchSort);

app.component("v-search-fields", SearchFields);
app.component("v-search-lightbox", SearchLightbox);
app.component("v-floor-selection", FloorSelection);

app.component("v-login", Login);

app.component("v-register-head", RegisterHead);
app.component("v-register-stepper", RegisterStepper);
app.component("v-submit-phone", SubmitPhone);
app.component("v-submit-verification-code", SubmitVerificationCode);
app.component("v-create-account", CreateAccount);
app.component("v-submit-email", SubmitEmail);
app.component("v-email-sent", EmailSent);
app.component("v-email-confirmed", EmailConfirmed);
app.component("v-setup-password", SetupPassword);
app.component("v-submit-personal-data", SubmitPersonalData);
app.component("v-submit-location-data", SubmitLocationData);
app.component("v-upload-customer-picture", UploadCustomerPicture);
app.component("v-welcome", Welcome);

app.component("v-add-listing-head", AddListingHead);

app.component("v-listing-price-information", ListingPriceInformation);
app.component("v-listing-price-history", ListingPriceHistory);
app.component("v-listing-contact", ListingContact);
app.component("v-listing-share", ListingShare);
app.component("v-listing-save", ListingSave);
app.component("v-listing-activate", ActivateListing);
app.component("v-listing-google-maps", ListingGoogleMaps);
app.component("v-search-results-google-maps", SearchResultsGoogleMaps);

app.component("v-icon", Icon);
app.component("v-form-radio", FormRadio);
app.component("v-form-input", FormInput);
app.component("v-checkbox", Checkbox);
app.component("v-accordion", Accordion);
app.component("v-dropdown", Dropdown);
app.component("v-dropdown-sm", DropdownSm);
app.component("v-csrf-token", CsrfToken);
app.component("v-session-message", SessionMessage);
app.component("v-alert", Alert);

app.component("v-add-listing-wizard", AddListingWizard);

app.component("v-my-listings", MyListings);
app.component("v-my-listing", MyListing);
app.component("v-edit-personal-data", EditPersonalData);
app.component("v-edit-location-data", EditLocationData);
app.component("v-edit-contact-data", EditContactData);
app.component("v-change-profile-picture", ChangeProfilePicture);
app.component("v-profile-settings", ProfileSettings);

// Vendor components
app.component("v-select", vSelect);
app.use(Clipboard)

app.use(VueClickAway);

app.use(VueGoogleMaps, {
    load: {
        key: window.googleApiKey,
        libraries: "places",
    },
});

app.use(RoutesPlugin);

// Design system
import DesignSystemIcons from "./DesignSystem/Icons.vue";

app.component("v-design-system-icons", DesignSystemIcons);

// Filters
app.config.globalProperties.$filters = {
    formatNumber(number) {
        return Intl.NumberFormat("sl-SI").format(number);
    },
};

//Expose global variable window to vue components
app.config.globalProperties.$csrfToken = window.csrfToken;
app.config.globalProperties.$assetsUrl = window.assetsUrl;
app.config.globalProperties.$globalFunctions = globalFunctions;

/**
 * The following block of code may be used to automatically register your
 * Vue components. It will recursively scan this directory for the Vue
 * components and automatically register them with their "basename".
 *
 * Eg. ./components/ExampleComponent.vue -> <example-component></example-component>
 */

// Object.entries(import.meta.glob('./**/*.vue', { eager: true })).forEach(([path, definition]) => {
//     app.component(path.split('/').pop().replace(/\.\w+$/, ''), definition.default);
// });

/**
 * Finally, we will attach the application instance to a HTML element with
 * an "id" attribute of "app". This element is included with the "auth"
 * scaffolding. Otherwise, you will need to add an element yourself.
 */

app.mount("#app");

setupGlide();
setupLazyLoad();
setupScrollPositionEvents();

// 100vh problem with iOS Safari
// https://dev.to/maciejtrzcinski/100vh-problem-with-ios-safari-3ge9
const appHeight = () => {
    const doc = document.documentElement;
    doc.style.setProperty("--app-height", `${window.innerHeight}px`);
};
window.addEventListener("resize", appHeight);
appHeight();
